// Cell renderers for the v2 grid. All read-only — click on a row
// bubbles to the row's onClick handler so the slide-over panel opens.
// Cells stop propagation only on interactive children (links, the
// star button) where a click means something local rather than
// "open this record".

import { Link } from 'react-router-dom';
import type { Contact } from '@personical/cc-shared-types';
import { Avatar } from '../../components/Avatar.js';
import { CallTreatmentBadge } from '../../components/CallTreatmentBadge.js';
import { displayNameFor } from '../../display.js';
import type { ContactGridContext } from '../columns/contactColumns.js';

export function NameCell({ row, currentOid }: { row: Contact; currentOid: string | null }) {
  const name = displayNameFor(row, currentOid);
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Avatar name={name} photoUrl={row.photoUrl ?? undefined} size="sm" />
      <span className="truncate font-medium text-strong">{name}</span>
      {row.callTreatment && row.callTreatment !== 'normal' && (
        <CallTreatmentBadge treatment={row.callTreatment} />
      )}
    </div>
  );
}

// Phase 2 ships read-only and we don't have a company-name lookup yet
// (Contact only carries companyId). Render a small "view" link to the
// company detail page; phase 3's cache layer will resolve the name.
export function CompanyRefCell({ row }: { row: Contact }) {
  if (!row.companyId) return <Empty />;
  return (
    <Link
      to={`/contacts/${row.companyId}`}
      className="text-mid hover:text-strong hover:underline truncate block text-xs"
      onClick={(e) => e.stopPropagation()}
    >
      View company
    </Link>
  );
}

export function EmailCell({ row }: { row: Contact }) {
  const primary = row.emails?.[0]?.address;
  if (!primary) return <Empty />;
  return (
    <a
      href={`mailto:${primary}`}
      className="text-mid hover:text-strong hover:underline truncate block"
      onClick={(e) => e.stopPropagation()}
    >
      {primary}
    </a>
  );
}

export function PhoneCell({ row }: { row: Contact }) {
  const primary = row.phoneNumbers?.[0];
  if (!primary) return <Empty />;
  const display = primary.e164 ?? primary.number;
  return (
    <a
      href={`tel:${display}`}
      className="text-mid hover:text-strong hover:underline truncate block tabular-nums"
      onClick={(e) => e.stopPropagation()}
    >
      {display}
    </a>
  );
}

export function TagsCell({ row }: { row: Contact }) {
  if (!row.tags?.length) return <Empty />;
  const visible = row.tags.slice(0, 3);
  const overflow = row.tags.length - visible.length;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {visible.map((t) => (
        <span
          key={t}
          className="rounded-md px-1.5 py-0.5 text-[11px] font-medium surface-muted text-mid"
        >
          {t}
        </span>
      ))}
      {overflow > 0 && <span className="text-xs text-faint">+{overflow}</span>}
    </div>
  );
}

export function StarCell({ row, ctx }: { row: Contact; ctx: ContactGridContext }) {
  const starred = ctx.currentOid ? (row.favoritedBy ?? []).includes(ctx.currentOid) : false;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (!ctx.currentOid) return;
        void ctx.onToggleStar(row.id, !starred);
      }}
      className={`inline-flex items-center justify-center rounded p-0.5 transition ${
        starred ? 'text-amber-500 hover:text-amber-600' : 'text-faint hover:text-mid'
      } disabled:opacity-50`}
      disabled={!ctx.currentOid}
      aria-pressed={starred}
      aria-label={starred ? 'Unfavourite' : 'Favourite'}
      title={starred ? 'Unfavourite' : 'Favourite'}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill={starred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15 9 22 10 17 15 18 22 12 19 6 22 7 15 2 10 9 9 12 2" />
      </svg>
    </button>
  );
}

export function DateCell({ value }: { value: string | null | undefined }) {
  if (!value) return <Empty />;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return <Empty />;
  return (
    <span className="text-mid tabular-nums">
      {d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
    </span>
  );
}

function Empty() {
  return <span className="text-faint">—</span>;
}
